import { useEffect, useState } from "react"

import { Flex, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"

import ButtonComponent from "../../Components/Button"
import Private from "../../Components/Private"
import Bone from "../../Components/Skeleton/Bone"
import { SubtitleSection } from "../../Components/SubtitleSection/SubtitleSection"
import withPermission from "../../hoc/withPermission"
import { useExternalDemoMode } from "../../hooks/useExternalDemoMode"
import { highAccessRoles } from "../../Utils/permission"
import BusinessDetailsModal from "./components/BusinessDetailsModal"
import { getBusinessDetails } from "./redux/businessDetails.thunks"

import "./styles.less"

const { Text } = Typography

const PermissionButton = withPermission(ButtonComponent, highAccessRoles)

const BusinessDetails = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [query] = useSearchParams()
  const externalDemoMode = useExternalDemoMode()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const user_id = query.get("user_id")
  const { isAdmin } = useSelector((state) => state.auth)
  const businessAccountRole = useSelector((state) => state.auth.userInfo?.ba_role?.id)
  const { businessDetails, loaded } = useSelector((state) => state.businessDetails)
  const userInfoForAdmin = useSelector((state) => state.user.userInfoForAdmin);
  const [openModal, setOpenModal] = useState(false)

  const isAdminViewingUser = isAdmin && !!user_id;
  const adminFullName = isAdminViewingUser
    ? [userInfoForAdmin?.first_name, userInfoForAdmin?.last_name].filter(Boolean).join(' ')
    : null;

  const renderValue = (value, skeletonWidth) => {
    if (!loaded) return <Bone width={skeletonWidth} height={20} />
    return <Text>{value ? <Private>{value}</Private> : "-"}</Text>
  }

  const onClose = () => setOpenModal(false)

  const onOpen = () => setOpenModal(true)

  useEffect(() => {
    dispatch(getBusinessDetails(isAdmin ? { user_id } : {}))
  }, [])
  return (
    <Flex gap={24} vertical className="business-details-block">
      <Flex gap={20} justify="space-between" align="flex-start" vertical={isMobile}>
        {isMobile && (
          <Flex gap={4} vertical>
            <Text className="header-text">Billing information</Text>
            <Text type="secondary">All information about your billing and invoices.</Text>
          </Flex>
        )}
        <SubtitleSection
          noMargin
          isMobile={isMobile}
          title={`Business details${adminFullName ? ` for ${adminFullName}` : ""}`}
          description={isAdminViewingUser
            ? userInfoForAdmin?.email
            : 'This information will be included in the invoices'
          }
        />
        {!isMobile && loaded && (
          <>
            {businessDetails ? (
              <PermissionButton
                size="middle"
                onClick={externalDemoMode ? null : onOpen}
                role={businessAccountRole}
              >
                Update information
              </PermissionButton>
            ) : (
              <PermissionButton
                type="primary"
                size="middle"
                onClick={externalDemoMode ? null : onOpen}
                role={businessAccountRole}
              >
                Provide information
              </PermissionButton>
            )}
          </>
        )}
      </Flex>
      {isMobile && loaded && (
        <>
          {businessDetails ? (
            <PermissionButton
              size="large"
              onClick={externalDemoMode ? null : onOpen}
              role={businessAccountRole}
            >
              Update information
            </PermissionButton>
          ) : (
            <PermissionButton
              type="primary"
              size="large"
              onClick={externalDemoMode ? null : onOpen}
              role={businessAccountRole}
            >
              Provide information
            </PermissionButton>
          )}
        </>
      )}
      <div className="details-table">
        <div className="details-field">
          <Text type="secondary">Business name</Text>
          {renderValue(businessDetails?.name, 140)}
        </div>
        <div className="details-field">
          <Text type="secondary">Address line 1</Text>
          {renderValue(businessDetails?.address_line_1, 160)}
        </div>
        {businessDetails?.address_line_2 && (
          <div className="details-field">
            <Text type="secondary">Address line 2</Text>
            <Text>
              <Private>{businessDetails.address_line_2}</Private>
            </Text>
          </div>
        )}
        <div className="details-field">
          <Text type="secondary">City/town</Text>
          {renderValue(businessDetails?.city, 100)}
        </div>
        <div className="details-field">
          <Text type="secondary">Country/region</Text>
          {renderValue(businessDetails?.country, 120)}
        </div>
        {businessDetails?.state && (
          <div className="details-field">
            <Text type="secondary">State</Text>
            <Text>
              <Private>{businessDetails.state}</Private>
            </Text>
          </div>
        )}
        <div className="details-field">
          <Text type="secondary">
            {businessDetails?.country === "United States"
              ? "Business registration number"
              : "Tax registration number (TRN)"}
          </Text>
          {renderValue(businessDetails?.registration_number, 130)}
        </div>
        <div className="details-field">
          <Text type="secondary">Postal code</Text>
          {renderValue(businessDetails?.postal_code, 80)}
        </div>
      </div>
      <BusinessDetailsModal open={openModal} onClose={onClose} />
    </Flex>
  )
}

export default BusinessDetails
